import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { listPosts, listCategories, absoluteUrl } from "@/lib/blog";
import * as Lucide from "lucide-react";
import { Search, ArrowRight, Clock, Calendar, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
};

const Icon = ({ name, className }) => {
  const C = Lucide[name] || Lucide.Tag;
  return <C className={className} />;
};

const PostCard = ({ post, featured = false }) => {
  const cover = absoluteUrl(post.cover_image?.url);
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-post-card-${post.slug}`}
      className={`group block rounded-3xl border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all overflow-hidden ${featured ? "lg:flex lg:items-stretch" : ""}`}
    >
      <div className={`relative aspect-[16/9] bg-gradient-to-br from-emerald-100 via-blue-100 to-violet-100 overflow-hidden ${featured ? "lg:w-1/2 lg:aspect-auto" : ""}`}>
        {cover ? (
          <img src={cover} alt={post.cover_image?.alt || post.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-emerald-600/40">
            <Icon name={post.category?.icon || "BookOpen"} className="h-14 w-14" />
          </div>
        )}
        {post.category && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-slate-800 ring-1 ring-slate-200 shadow-sm">
            <Icon name={post.category.icon || "Tag"} className="h-3 w-3 text-emerald-600" />
            {post.category.name}
          </span>
        )}
      </div>
      <div className={`p-5 sm:p-6 ${featured ? "lg:w-1/2 lg:flex lg:flex-col lg:justify-center lg:p-8" : ""}`}>
        {featured && <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-600 mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3" />Featured article</div>}
        <h3 className={`font-extrabold text-slate-900 tracking-tight group-hover:text-emerald-700 transition-colors ${featured ? "text-2xl sm:text-3xl leading-tight" : "text-lg leading-snug"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
          {post.title}
        </h3>
        {post.excerpt && <p className="mt-2 text-sm text-slate-600 line-clamp-2">{post.excerpt}</p>}
        <div className="mt-4 flex items-center gap-3 text-[11.5px] text-slate-500">
          {post.author?.name && <span className="font-semibold text-slate-700">{post.author.name}</span>}
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(post.published_at)}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{post.reading_time_minutes || 1} min</span>
        </div>
      </div>
    </Link>
  );
};

export default function BlogListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], featured: null, total: 0, total_pages: 0 });
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";
  const tag = searchParams.get("tag") || "";
  const sort = searchParams.get("sort") || "latest";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const [draftQ, setDraftQ] = useState(q);

  const load = async () => {
    setLoading(true);
    try {
      const [posts, c] = await Promise.all([
        listPosts({ q: q || undefined, category: category || undefined, tag: tag || undefined, sort, page, page_size: 9 }),
        listCategories(),
      ]);
      setData(posts);
      setCats(c);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [q, category, tag, sort, page]);

  // Imperatively override head meta tags so a previous SeoHead mutation (from Landing)
  // doesn't leave stale og:title / description / canonical values when SPA-navigating.
  useEffect(() => {
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const title = "Wappit Blog — WhatsApp Business API insights, guides & case studies";
    const desc = "Practical guides, product updates and case studies on WhatsApp Business API, chatbots, templates and customer engagement — from the Wappit editorial team.";
    const canonical = typeof window !== "undefined" ? `${window.location.origin}/blog` : "/blog";
    document.title = title;
    setMeta("description", desc);
    setMeta("og:type", "website", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", canonical, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setLink("canonical", canonical);
  }, []);

  const update = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v) next.delete(k);
      else next.set(k, v);
    });
    if (!patch.page) next.delete("page");
    setSearchParams(next, { replace: false });
  };

  const submitSearch = (e) => { e.preventDefault(); update({ q: draftQ, page: null }); };

  return (
    <>
      <Helmet>
        <title>Wappit Blog — WhatsApp Business API insights, guides &amp; case studies</title>
        <meta name="description" content="Practical guides, product updates and case studies on WhatsApp Business API, chatbots, templates and customer engagement — from the Wappit editorial team." />
        <link rel="canonical" href={typeof window !== "undefined" ? `${window.location.origin}/blog` : "/blog"} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Wappit Blog" />
        <meta property="og:description" content="Practical guides, product updates and case studies on WhatsApp Business API." />
        <link rel="alternate" type="application/rss+xml" title="Wappit Blog RSS" href={`${process.env.REACT_APP_BACKEND_URL}/api/blog/feed.rss`} />
      </Helmet>

      <Navbar onBookDemo={() => {}} />

      <main className="bg-slate-50 min-h-screen">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-10 sm:pt-32 sm:pb-14">
          <div className="absolute -top-32 -right-20 h-[420px] w-[520px] rounded-full bg-emerald-200/30 blur-3xl pointer-events-none" />
          <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-semibold text-slate-700">
              <Sparkles className="h-3.5 w-3.5 text-emerald-500" /> Wappit Blog
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Field notes for teams who run on <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">WhatsApp</span>
            </h1>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-600">
              Practical guides, product updates and case studies from the team that&apos;s onboarded 900+ businesses to the Official WhatsApp Business API.
            </p>

            <form onSubmit={submitSearch} className="mt-7 flex max-w-xl gap-2" data-testid="blog-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={draftQ}
                  onChange={(e) => setDraftQ(e.target.value)}
                  placeholder="Search articles…"
                  data-testid="blog-search-input"
                  className="pl-9 h-11 rounded-full bg-white"
                />
              </div>
              <Button type="submit" data-testid="blog-search-submit" className="rounded-full bg-slate-900 hover:bg-slate-800 text-white h-11 px-5 font-semibold">Search</Button>
            </form>
          </div>
        </section>

        {/* Filters */}
        <section className="border-t border-b border-slate-200/70 bg-white/70 backdrop-blur sticky top-16 z-30">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3 overflow-x-auto no-scrollbar" data-testid="blog-filters">
            <button onClick={() => update({ category: null, tag: null })} data-testid="blog-cat-all" className={`px-3 h-9 rounded-full text-xs font-semibold whitespace-nowrap ${!category ? "bg-slate-900 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
              All
            </button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => update({ category: c.slug, tag: null, page: null })} data-testid={`blog-cat-${c.slug}`} className={`px-3 h-9 rounded-full text-xs font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${category === c.slug ? "bg-slate-900 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
                <Icon name={c.icon || "Tag"} className="h-3 w-3" />
                {c.name}
                <span className="text-[10px] opacity-70">{c.post_count}</span>
              </button>
            ))}
            <div className="ml-auto inline-flex items-center gap-2 text-xs">
              <span className="text-slate-500">Sort:</span>
              <select
                value={sort}
                onChange={(e) => update({ sort: e.target.value, page: null })}
                data-testid="blog-sort"
                className="h-8 rounded-full bg-white ring-1 ring-slate-200 px-2 text-xs font-semibold"
              >
                <option value="latest">Latest</option>
                <option value="popular">Popular</option>
              </select>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-10 sm:py-14 space-y-10">
          {data.featured && page === 1 && !q && !category && !tag && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="blog-featured">
              <PostCard post={data.featured} featured />
            </motion.div>
          )}

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-3xl border border-slate-200 bg-white h-72 animate-pulse" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center"><Search className="h-7 w-7" /></div>
              <p className="mt-4 text-slate-600">No articles match this filter yet.</p>
              <button onClick={() => { setDraftQ(""); update({ q: null, category: null, tag: null, sort: null, page: null }); }} className="mt-3 text-sm font-semibold text-emerald-700">Clear filters</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {data.items
                .filter((p) => !(data.featured && page === 1 && !q && !category && !tag && p.id === data.featured.id))
                .map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}

          {data.total_pages > 1 && (
            <nav className="flex items-center justify-center gap-2" data-testid="blog-pagination">
              <Button variant="outline" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} className="rounded-full h-9" data-testid="blog-prev"><ChevronLeft className="h-4 w-4" /> Prev</Button>
              <span className="text-xs font-semibold text-slate-600">Page {page} of {data.total_pages}</span>
              <Button variant="outline" disabled={page >= data.total_pages} onClick={() => update({ page: String(page + 1) })} className="rounded-full h-9" data-testid="blog-next">Next <ChevronRight className="h-4 w-4" /></Button>
            </nav>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
